/**
 * Platform fonts: a curated default set of fonts available to every
 * workspace, served from Google Fonts.
 *
 * Two jobs:
 *
 * 1. Back web-safe names. Ad mockups routinely specify proprietary families
 *    (Arial, Times New Roman, Georgia, …) that never exist as uploaded brand
 *    assets, so text renders on an unpinned system fallback — or not at all.
 *    Each family below lists the proprietary names it substitutes for as
 *    `synonyms`; font resolution rewrites those hint names to the backed
 *    family. The core six are true metric twins (Google's ChromeOS core
 *    fonts); the rest are the closest open substitutes by design, not by
 *    metrics.
 * 2. Give font-less workspaces something to type with. Families with no
 *    synonyms are a small general-purpose pool the agent can pick from when
 *    the brand profile carries no fonts of its own.
 *
 * `platformCatalogResolvedFonts` (resolved-font.ts) turns this catalog into
 * `global`-scoped resolved fonts; `resolveFonts` unions them into every
 * workspace's resolved fonts at read time (no per-workspace seeding), letting a
 * workspace's own upload or declaration of the same family win. Swapping the
 * backing to self-hosted files later only changes the faces that catalog
 * projection emits.
 *
 * Browser-safe: no server logger or Node built-ins, same constraint as
 * `font-names.ts`.
 */

export type PlatformFontFamily = {
  /** Typographic family name as Google Fonts spells it — what the agent writes in `font-family`. */
  family: string;
  /**
   * Proprietary families this one substitutes for. Used to resolve hint/spec
   * names ("Arial") to the backed platform family. Empty for the
   * general-purpose pool.
   */
  synonyms: string[];
  /**
   * Google Fonts variant strings requested in the CSS URL ("regular",
   * "italic", "700", "700italic"). Must be variants the family actually
   * ships — Google answers 400 for the whole URL otherwise.
   */
  variants: string[];
};

const UPRIGHT_AND_ITALIC = ["regular", "italic", "700", "700italic"];
const UPRIGHT_ONLY = ["regular", "700"];

/**
 * The v1 platform set. Ordered: metric twins of the core web-safe families,
 * closest open substitutes for other common proprietary names, then the
 * general-purpose pool.
 */
export const PLATFORM_FONTS: PlatformFontFamily[] = [
  {
    family: "Arimo",
    synonyms: ["Arial", "Helvetica", "Helvetica Neue", "Liberation Sans"],
    variants: UPRIGHT_AND_ITALIC,
  },
  {
    family: "Tinos",
    synonyms: ["Times New Roman", "Times", "Liberation Serif"],
    variants: UPRIGHT_AND_ITALIC,
  },
  {
    family: "Cousine",
    synonyms: ["Courier New", "Courier", "Liberation Mono"],
    variants: UPRIGHT_AND_ITALIC,
  },
  { family: "Gelasio", synonyms: ["Georgia"], variants: UPRIGHT_AND_ITALIC },
  { family: "Carlito", synonyms: ["Calibri"], variants: UPRIGHT_AND_ITALIC },
  { family: "Caladea", synonyms: ["Cambria"], variants: UPRIGHT_AND_ITALIC },
  {
    family: "Comic Neue",
    synonyms: ["Comic Sans MS", "Comic Sans"],
    variants: UPRIGHT_AND_ITALIC,
  },
  { family: "Anton", synonyms: ["Impact"], variants: ["regular"] },
  {
    family: "Libre Baskerville",
    synonyms: ["Baskerville", "Baskerville Old Face"],
    variants: ["regular", "italic", "700"],
  },
  {
    family: "EB Garamond",
    synonyms: ["Garamond", "Adobe Garamond Pro"],
    variants: UPRIGHT_AND_ITALIC,
  },
  {
    family: "Libre Franklin",
    synonyms: ["Franklin Gothic", "Franklin Gothic Medium"],
    variants: UPRIGHT_AND_ITALIC,
  },
  {
    family: "Jost",
    synonyms: ["Futura", "Futura PT"],
    variants: UPRIGHT_AND_ITALIC,
  },
  {
    family: "Nunito Sans",
    synonyms: ["Avenir", "Avenir Next"],
    variants: UPRIGHT_AND_ITALIC,
  },
  {
    family: "Montserrat",
    synonyms: ["Proxima Nova", "Gotham"],
    variants: UPRIGHT_AND_ITALIC,
  },
  { family: "Inter", synonyms: [], variants: UPRIGHT_AND_ITALIC },
  { family: "Roboto", synonyms: [], variants: UPRIGHT_AND_ITALIC },
  { family: "Open Sans", synonyms: [], variants: UPRIGHT_AND_ITALIC },
  { family: "Lato", synonyms: [], variants: UPRIGHT_AND_ITALIC },
  { family: "Poppins", synonyms: [], variants: UPRIGHT_AND_ITALIC },
  { family: "Playfair Display", synonyms: [], variants: UPRIGHT_AND_ITALIC },
  { family: "Oswald", synonyms: [], variants: UPRIGHT_ONLY },
  { family: "Bebas Neue", synonyms: [], variants: ["regular"] },
];

/**
 * Weight/style/width tokens stripped from the tail of a font name when
 * computing its canonical family key. Mirrors the matcher's suffix set in
 * `bedrock-agent-service` (`image-engine/core/font-match/matcher.ts`), which
 * cannot be imported from lib.
 */
const FAMILY_SUFFIX_TOKENS = new Set([
  "thin",
  "extralight",
  "ultralight",
  "light",
  "regular",
  "normal",
  "book",
  "medium",
  "semibold",
  "demibold",
  "demi",
  "bold",
  "extrabold",
  "ultrabold",
  "heavy",
  "black",
  "italic",
  "oblique",
  "condensed",
  "extended",
  "expanded",
  "narrow",
  "wide",
]);

/**
 * Qualifier tokens that only modify a following suffix token ("Extra" in
 * "Extra Bold"). Dropped when camelCase/separator splitting produced them as
 * standalone tokens right before a stripped suffix.
 */
const SUFFIX_QUALIFIER_TOKENS = new Set(["extra", "ultra", "semi", "demi"]);

/**
 * Canonical identity of a font family name: split on separators and
 * camelCase boundaries, drop trailing weight/style tokens, join lowercase.
 * `"Arimo-Bold"`, `"arimo bold italic"` and `"Arimo"` all map to `"arimo"`.
 */
export function canonicalFamilyKey(raw: string): string {
  const tokens = raw
    .split(/[^a-zA-Z0-9]+|(?<=[a-z])(?=[A-Z])/)
    .filter(Boolean)
    .map((t) => t.toLowerCase());
  let strippedSuffix = false;
  while (tokens.length > 1) {
    const last = tokens.at(-1) ?? "";
    if (FAMILY_SUFFIX_TOKENS.has(last)) {
      tokens.pop();
      strippedSuffix = true;
    } else if (strippedSuffix && SUFFIX_QUALIFIER_TOKENS.has(last)) {
      tokens.pop();
    } else {
      break;
    }
  }
  return tokens.join("");
}

const SYNONYM_TO_FAMILY: ReadonlyMap<string, PlatformFontFamily> = new Map(
  PLATFORM_FONTS.flatMap((fam) => [
    [canonicalFamilyKey(fam.family), fam] as const,
    ...fam.synonyms.map((s) => [canonicalFamilyKey(s), fam] as const),
  ]),
);

/**
 * The platform family backing a requested family name, matching either the
 * platform name itself ("Arimo") or a synonym ("Arial", "Helvetica Neue").
 * Returns undefined for names the platform set doesn't cover.
 */
export function platformFamilyFor(
  requestedFamily: string,
): PlatformFontFamily | undefined {
  return SYNONYM_TO_FAMILY.get(canonicalFamilyKey(requestedFamily));
}
